import { OptionUnitRestriction } from '@linktivity/ars-neo';
import {
  OPTION_UNIT_RESTRICTION_STATUS,
  type OptionUnitRestrictionStatus
} from '../constants';
import { normalizeOptionQuantity } from './optionUnitWeight';

export type { OptionUnitRestrictionStatus };

export interface OptionUnitRestrictionResult {
  hasMaximum: boolean;
  status: OptionUnitRestrictionStatus;
  /**
   * Weighted units still missing (shortage) or over the allowance (excess),
   * on the same scale as `mainQuantity`. Deliberately not a count of option
   * items: an item count only exists per product ratio, so each option card
   * would name a different number for one shared rule and none of them would
   * match the payment footer, which spans products with their own ratios.
   */
  difference: number;
}

export interface GetOptionUnitRestrictionResultOptions {
  restriction?: OptionUnitRestriction;
  mainQuantity: number;
  /** Weighted option total: each product's units times its own unitRatio. */
  optionQuantity: number;
}

/** Rounded up: a partial unit still leaves the rule unmet. */
const getUnitDifference = (weightedDifference: number): number =>
  Math.ceil(normalizeOptionQuantity(weightedDifference));

export const sumQuantities = (
  quantities: ReadonlyArray<number | null | undefined>
): number =>
  normalizeOptionQuantity(
    quantities.reduce<number>(
      (total, quantity) => total + normalizeOptionQuantity(quantity),
      0
    )
  );

export const getOptionUnitRestrictionResult = ({
  restriction,
  mainQuantity,
  optionQuantity
}: GetOptionUnitRestrictionResultOptions): OptionUnitRestrictionResult => {
  const hasMinimum =
    restriction === OptionUnitRestriction.OptionUnitRestrictionEqual ||
    restriction ===
      OptionUnitRestriction.OptionUnitRestrictionGreaterThanOrEqual;
  const hasMaximum =
    restriction === OptionUnitRestriction.OptionUnitRestrictionEqual ||
    restriction === OptionUnitRestriction.OptionUnitRestrictionLessThanOrEqual;
  const normalizedMainQuantity = normalizeOptionQuantity(mainQuantity);
  const normalizedOptionQuantity = normalizeOptionQuantity(optionQuantity);

  // An empty plan outranks every other outcome, whatever the restriction type.
  // A minimum is trivially met by an empty plan and a maximum caps the
  // allowance at 0, so both would either fall silent or name an option count
  // while the only fix is picking plan participants — name that instead.
  if ((hasMinimum || hasMaximum) && normalizedMainQuantity <= 0) {
    return {
      hasMaximum,
      status: OPTION_UNIT_RESTRICTION_STATUS.MAIN_QUANTITY_MISSING,
      difference: 0
    };
  }

  if (hasMinimum && normalizedOptionQuantity < normalizedMainQuantity) {
    return {
      hasMaximum,
      status: OPTION_UNIT_RESTRICTION_STATUS.SHORTAGE,
      difference: getUnitDifference(
        normalizedMainQuantity - normalizedOptionQuantity
      )
    };
  }

  if (hasMaximum && normalizedOptionQuantity > normalizedMainQuantity) {
    return {
      hasMaximum,
      status: OPTION_UNIT_RESTRICTION_STATUS.EXCESS,
      difference: getUnitDifference(
        normalizedOptionQuantity - normalizedMainQuantity
      )
    };
  }

  return {
    hasMaximum,
    status: OPTION_UNIT_RESTRICTION_STATUS.VALID,
    difference: 0
  };
};

export type OptionUnitRestrictionMessageKey =
  | 'share.payment.optionQuantityShortage'
  | 'share.payment.optionQuantityPartialShortage'
  | 'share.payment.optionQuantityExcess'
  | 'share.payment.planQuantityRequired';

export interface OptionUnitRestrictionMessageDescriptor {
  key: OptionUnitRestrictionMessageKey;
  quantity: number;
}

/**
 * `hasOwnSelection` — whether the calling view holds option picks of its own
 * that count toward the rule, which is what lets it say "your current
 * selection" and be understood. Only the option card does: it owns the steppers
 * the wording points at. Views spanning every option product, like the payment
 * footer, pass nothing and keep the plain wording.
 */
export const getOptionUnitRestrictionMessageDescriptor = (
  result: OptionUnitRestrictionResult,
  { hasOwnSelection = false }: { hasOwnSelection?: boolean } = {}
): OptionUnitRestrictionMessageDescriptor | undefined => {
  if (result.status === OPTION_UNIT_RESTRICTION_STATUS.MAIN_QUANTITY_MISSING) {
    // The plan quantity is what has to change, so no option count is named.
    return {
      key: 'share.payment.planQuantityRequired',
      quantity: 0
    };
  }
  if (result.status === OPTION_UNIT_RESTRICTION_STATUS.SHORTAGE) {
    // Once the view's own options are picked the shortfall is what's left to add
    // on top of them, not the whole requirement — say so, or the untouched
    // wording reads as if nothing counted.
    return {
      key: hasOwnSelection
        ? 'share.payment.optionQuantityPartialShortage'
        : 'share.payment.optionQuantityShortage',
      quantity: result.difference
    };
  }
  if (result.status === OPTION_UNIT_RESTRICTION_STATUS.EXCESS) {
    return {
      key: 'share.payment.optionQuantityExcess',
      quantity: result.difference
    };
  }
  return undefined;
};
